import { useEffect, useState } from "react";
import axios from "axios";
import { X, Check, Sparkles, Loader2 } from "lucide-react";
import { PRICING } from "@/constants/testIds";
import { adminApi } from "@/lib/admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Precio y moneda son SOLO informativos aquí — la fuente de verdad vive en el
// backend (`CHECKOUT_AMOUNT_EUR = 39.0`). Nunca los mandamos al backend en el
// payload de checkout, para evitar manipulación desde el navegador.
const PRICE_LABEL = "39€";
const FEATURES = [
    "URL propia bajo lovingangels.es/w/tu-boda",
    "Editor con vista previa en directo",
    "Sistema de RSVP con dietas por invitado",
    "Panel de administración con código de boda",
    "Sin anuncios, sin suscripciones, sin caducidad",
];

export default function PricingSheet({ open, onClose, wedding }) {
    const [status, setStatus] = useState("idle"); // idle | creating | error
    const [error, setError] = useState(null);
    const [slugPreview, setSlugPreview] = useState("");

    useEffect(() => {
        if (!open) return;
        setStatus("idle");
        setError(null);
        (async () => {
            try {
                const res = await axios.get(`${API}/slug-preview`, {
                    params: {
                        primary: wedding.primary_name || "",
                        secondary: wedding.secondary_name || "",
                    },
                });
                setSlugPreview(res.data.slug);
            } catch (e) {
                console.warn("slug preview failed", e);
            }
        })();
    }, [open, wedding]);

    const handleCheckout = async () => {
        setStatus("creating");
        setError(null);
        try {
            // Step 1: save the current draft to the user's authenticated
            // wedding (PUT). This returns the wedding with its id.
            const saved = await adminApi.updateWedding(stripFields(wedding));
            // Step 2: ask the backend for a Stripe Checkout Session tied
            // to that wedding_id + the authenticated user_id.
            const origin_url =
                typeof window !== "undefined"
                    ? window.location.origin
                    : "";
            const session = await axios.post(
                `${API}/checkout/create-session`,
                {
                    wedding_id: saved.id,
                    origin_url,
                },
                { withCredentials: true },
            );
            // Step 3: redirect to Stripe hosted checkout.
            if (session.data?.url) {
                window.location.href = session.data.url;
            } else {
                throw new Error("Sin URL de Stripe");
            }
        } catch (e) {
            console.error("checkout failed", e);
            setError(
                "No hemos podido iniciar el pago. Inténtalo de nuevo.",
            );
            setStatus("idle");
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid={PRICING.sheet}
            className="fixed inset-0 z-[60] bg-[rgba(20,16,12,0.55)] backdrop-blur-sm flex md:items-center items-end justify-center"
        >
            <div className="bg-[var(--bg)] w-full md:max-w-[560px] md:rounded-3xl rounded-t-3xl shadow-2xl md:my-6 md:mx-6 max-h-[94dvh] overflow-y-auto tpl-anim">
                <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-[var(--line)] sticky top-0 bg-[var(--bg)]/95 backdrop-blur z-10">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)]">
                            Publicar tu web
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl mt-1 leading-none">
                            Un solo pago, para siempre
                        </h2>
                    </div>
                    <button
                        data-testid={PRICING.close}
                        onClick={onClose}
                        aria-label="Cerrar"
                        className="w-10 h-10 rounded-full border border-[var(--line)] inline-flex items-center justify-center bg-white"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 md:px-10 py-8">
                    {slugPreview && (
                        <div className="mb-6 flex items-center gap-2 chip">
                            <Sparkles size={12} />
                            Tu URL será{" "}
                            <code className="font-mono text-[var(--ink)] ml-1">
                                /w/{slugPreview}
                            </code>
                        </div>
                    )}

                    <div className="rounded-3xl border border-[var(--ink)] bg-[var(--ink)] text-[#fbf9f6] p-8 shadow-2xl">
                        <div className="text-[11px] uppercase tracking-[0.22em] opacity-70">
                            Acceso completo
                        </div>
                        <h3 className="font-serif text-3xl mt-2">
                            Publica tu web
                        </h3>
                        <div className="mt-4 flex items-baseline gap-1">
                            <span className="font-serif text-6xl leading-none">
                                {PRICE_LABEL}
                            </span>
                            <span className="text-sm ml-2 opacity-70">
                                pago único · sin renovaciones
                            </span>
                        </div>
                        <ul className="mt-6 space-y-3 text-sm">
                            {FEATURES.map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-2"
                                >
                                    <Check
                                        size={14}
                                        className="mt-0.5 shrink-0 opacity-80"
                                    />
                                    <span className="opacity-95">{f}</span>
                                </li>
                            ))}
                        </ul>

                        {error && (
                            <div className="mt-5 text-xs bg-red-500/10 border border-red-400/40 rounded-xl px-4 py-2.5 text-red-200">
                                {error}
                            </div>
                        )}

                        <button
                            data-testid={PRICING.checkoutButton}
                            onClick={handleCheckout}
                            disabled={status === "creating"}
                            className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-medium bg-[#fbf9f6] text-[var(--ink)] hover:opacity-90 disabled:opacity-60 transition-all"
                        >
                            {status === "creating" ? (
                                <>
                                    <Loader2
                                        size={16}
                                        className="animate-spin"
                                    />
                                    Redirigiendo a Stripe...
                                </>
                            ) : (
                                <>Pagar {PRICE_LABEL} y publicar</>
                            )}
                        </button>
                        <p className="text-[11px] text-center mt-3 opacity-60">
                            Pago seguro procesado por Stripe. No guardamos
                            datos de tu tarjeta.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Strip UI-only fields and only send what the backend expects.
function stripFields(data) {
    const {
        primary_name = "",
        secondary_name = "",
        wedding_date = "",
        tagline = "",
        message = "",
        template = "classic-elegant",
        cover_image = "",
        ceremony,
        party,
        iban = "",
        ibanEnabled = false,
        busEnabled = false,
        busDeparture = "",
        busReturn = "",
        countdownEnabled = true,
        messageEnabled = true,
    } = data;
    const cleanVenue = (v) => ({
        name: v?.name || "",
        address: v?.address || "",
        map_url: v?.map_url || "",
        time: v?.time || "",
    });
    return {
        primary_name,
        secondary_name,
        wedding_date,
        tagline,
        message,
        template,
        cover_image,
        ceremony: cleanVenue(ceremony),
        party: cleanVenue(party),
        iban,
        ibanEnabled,
        busEnabled,
        busDeparture,
        busReturn,
        countdownEnabled,
        messageEnabled,
    };
}
